import type { Rarity } from "./omikuji";

/**
 * Phase 1: localStorage ベースの暫定ストア。
 * Track B の Cloud Functions / Firestore 接続が出来たら全置換する想定で
 * 関数インターフェースは将来 async に upgrade しやすい形にしておく。
 */

const KEY_PROFILE = "musubi.profile";
const KEY_DRAWS = "musubi.draws";
const KEY_PAIRS = "musubi.pairs";

export type StoredProfile = {
  uid: string;
  displayName: string;
  birthdate?: string;
  locale: "ja" | "en";
  isUnlocked: boolean;
  createdAt: string;
};

export type StoredDraw = {
  drawId: string;
  /** 仕様 02_DATA_MODEL.md §2.3 準拠: "first_pair" は廃止、pair + isFirstPair で表現 */
  mode: "solo" | "pair" | "unlock_special";
  isFirstPair?: boolean;
  pairId?: string;
  date: string; // YYYY-MM-DD
  drawnAt: string; // ISO
  rarity: Rarity;
  bodyText: string;
  bodyTextEn: string;
  themeAuto: string;
  partnerName?: string;
};

export type StoredPair = {
  pairId: string;
  partnerName: string;
  relationshipType: "lover" | "friend" | "family" | "colleague" | "other";
  totalDraws: number;
  currentStreak: number;
  longestStreak: number;
  lastDrawDate?: string;
  unlockedTiers: number[];
  createdAt: string;
};

// ---- helpers ----

function read<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write<T>(key: string, value: T) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota / private mode — fail silent */
  }
}

export function todayKey(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

let monotonicCounter = 0;
function newId(): string {
  monotonicCounter += 1;
  return `${Date.now().toString(36)}_${monotonicCounter.toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 6)}`;
}

function nowIso(): string {
  // 同一ミリ秒でも単調増加するよう、ナノ秒相当を追加
  monotonicCounter += 1;
  const base = new Date().toISOString();
  return base.replace("Z", `.${monotonicCounter.toString().padStart(6, "0")}Z`);
}

// ---- profile ----

export function getProfile(): StoredProfile | null {
  return read<StoredProfile | null>(KEY_PROFILE, null);
}

export function saveProfile(p: Partial<StoredProfile>): StoredProfile {
  const prev = getProfile();
  const merged: StoredProfile = {
    uid: prev?.uid ?? newId(),
    displayName: p.displayName ?? prev?.displayName ?? "あなた",
    birthdate: p.birthdate ?? prev?.birthdate,
    locale: p.locale ?? prev?.locale ?? "ja",
    isUnlocked: p.isUnlocked ?? prev?.isUnlocked ?? false,
    createdAt: prev?.createdAt ?? new Date().toISOString(),
  };
  write(KEY_PROFILE, merged);
  return merged;
}

// ---- draws ----

export function getDraws(): StoredDraw[] {
  return read<StoredDraw[]>(KEY_DRAWS, []);
}

export function getDrawsByPair(pairId: string): StoredDraw[] {
  return getDraws()
    .filter((d) => d.pairId === pairId)
    .sort((a, b) => b.drawnAt.localeCompare(a.drawnAt));
}

export function getTodaySoloDraw(): StoredDraw | undefined {
  const today = todayKey();
  return getDraws().find((d) => d.mode === "solo" && d.date === today);
}

export function appendDraw(
  input: Omit<StoredDraw, "drawId" | "drawnAt" | "date"> & {
    date?: string;
  },
): StoredDraw {
  const draw: StoredDraw = {
    drawId: newId(),
    drawnAt: nowIso(),
    date: input.date ?? todayKey(),
    ...input,
  };
  const all = getDraws();
  all.push(draw);
  write(KEY_DRAWS, all);

  // ペア統計を更新
  if (draw.pairId) {
    bumpPairStats(draw.pairId, draw.date);
  }
  return draw;
}

// ---- pairs ----

const UNLOCK_TIERS = [5, 10, 30, 50, 100, 365] as const;

export function getPairs(): StoredPair[] {
  return read<StoredPair[]>(KEY_PAIRS, []);
}

export function getPair(pairId: string): StoredPair | undefined {
  return getPairs().find((p) => p.pairId === pairId);
}

export function ensurePair(input: {
  pairId: string;
  partnerName: string;
  relationshipType?: StoredPair["relationshipType"];
}): StoredPair {
  const all = getPairs();
  const existing = all.find((p) => p.pairId === input.pairId);
  if (existing) return existing;
  const p: StoredPair = {
    pairId: input.pairId,
    partnerName: input.partnerName,
    relationshipType: input.relationshipType ?? "friend",
    totalDraws: 0,
    currentStreak: 0,
    longestStreak: 0,
    unlockedTiers: [],
    createdAt: new Date().toISOString(),
  };
  all.push(p);
  write(KEY_PAIRS, all);
  return p;
}

function bumpPairStats(pairId: string, drawDate: string) {
  const all = getPairs();
  const idx = all.findIndex((p) => p.pairId === pairId);
  if (idx < 0) return;
  const p = all[idx];
  // streak 計算: 前回が前日なら +1、同日なら据え置き、それ以外は 1 に reset
  const prev = p.lastDrawDate;
  let nextStreak = p.currentStreak;
  if (!prev) nextStreak = 1;
  else if (prev === drawDate) nextStreak = Math.max(1, p.currentStreak);
  else {
    const prevDate = new Date(prev + "T00:00:00");
    const curDate = new Date(drawDate + "T00:00:00");
    const diffDays = Math.round(
      (curDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24),
    );
    nextStreak = diffDays === 1 ? p.currentStreak + 1 : 1;
  }

  const totalDraws = p.totalDraws + 1;
  const unlockedTiers = UNLOCK_TIERS.filter((t) => totalDraws >= t);

  all[idx] = {
    ...p,
    totalDraws,
    currentStreak: nextStreak,
    longestStreak: Math.max(p.longestStreak, nextStreak),
    lastDrawDate: drawDate,
    unlockedTiers,
  };
  write(KEY_PAIRS, all);
}

export function getNextTier(
  pair: StoredPair,
): { tier: number; remaining: number } | null {
  const next = UNLOCK_TIERS.find((t) => pair.totalDraws < t);
  if (!next) return null;
  return { tier: next, remaining: next - pair.totalDraws };
}

export const TIERS = UNLOCK_TIERS;

// ---- invitation code ----

const KEY_INVITE = "musubi.invite";

export type StoredInvite = {
  code: string;
  createdAt: string;
  /** 受諾された相手 (Phase 2 で実通信) */
  acceptedBy?: string;
};

function generateInviteCode(): string {
  // 短く読み易く、Web Share の URL 末尾に使える形 (8 chars, ambig-free)
  const alphabet = "abcdefghjkmnpqrstuvwxyz23456789";
  let s = "";
  for (let i = 0; i < 8; i++) {
    s += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return s;
}

export function getOrCreateInvite(): StoredInvite {
  const existing = read<StoredInvite | null>(KEY_INVITE, null);
  if (existing && existing.code) return existing;
  const fresh: StoredInvite = {
    code: generateInviteCode(),
    createdAt: new Date().toISOString(),
  };
  write(KEY_INVITE, fresh);
  return fresh;
}

export function rotateInvite(): StoredInvite {
  const fresh: StoredInvite = {
    code: generateInviteCode(),
    createdAt: new Date().toISOString(),
  };
  write(KEY_INVITE, fresh);
  return fresh;
}

// ---- 過去ドロー — 巫女セリフ用 ----

export function getLastDraw(): StoredDraw | undefined {
  const all = getDraws();
  if (all.length === 0) return undefined;
  return [...all].sort((a, b) => b.drawnAt.localeCompare(a.drawnAt))[0];
}

/** 全ペアでの最大累積回数 (巫女アンロック判定用) */
export function getMaxPairTotal(): number {
  const ps = getPairs();
  if (ps.length === 0) return 0;
  return Math.max(...ps.map((p) => p.totalDraws));
}

// ---- mock seed (Phase 1 開発体験のため、初回ペア前提) ----

export function ensureSeedPair(): StoredPair {
  const seeded = ensurePair({
    pairId: "seed_yu",
    partnerName: "ゆう",
    relationshipType: "friend",
  });
  return seeded;
}

// ---- dev / settings 用 ----

export function clearAll() {
  if (typeof window === "undefined") return;
  localStorage.removeItem(KEY_PROFILE);
  localStorage.removeItem(KEY_DRAWS);
  localStorage.removeItem(KEY_PAIRS);
  localStorage.removeItem(KEY_INVITE);
}
